import React, { useMemo, useEffect, useRef } from 'react';
import {
    Box, Button, Grid, Typography, Container,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import { platformColor } from './constants';
import { DashboardLoading, DashboardEmpty } from './DashboardStates';
import PageAlert from '../PageAlert';
import {
    TrendingUp as TrendingUpIcon,
    ThumbUp as ThumbUpIcon,
    Comment as CommentIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import StatCard from './StatCard';
import { ViewDistributionPieChart, ViewsBarChart } from './ViewsCharts';
import { LikesCommentsChart, EngagementRateChart } from './EngagementCharts';
import PlatformTable from './PlatformTable';
import ConnectPlatformsCTA from '../ConnectPlatformsCTA';

/**
 * Dashboard orchestrator.
 */
const Dashboard = ({ analytics, loading, onRefresh, error }) => {
    const hasAutoFetched = useRef(false);
    // Unified error extraction
    const errorMsg = typeof error === 'string' ? error : (error?.message || '');

    // Detect setup/connectivity state
    const isNoAccountsError = !!errorMsg.toLowerCase().match(/no .*accounts|link.*platforms|social.*media.*setup/i);

    // One-shot auto-fetch on entry
    useEffect(() => {
        if ((!analytics || analytics.length === 0) && !loading && !errorMsg && !hasAutoFetched.current && onRefresh) {
            hasAutoFetched.current = true;
            onRefresh();
        }
    }, [onRefresh, analytics, loading, errorMsg]);

    // ── Derived data ────────────────────────────────────────────────────────
    const chartData = useMemo(() =>
        (analytics || [])
            .filter(item => item?.platform)
            .map((item, index) => ({
                platform: item.platform.charAt(0).toUpperCase() + item.platform.slice(1),
                rawPlatform: item.platform,
                views: Math.max(0, item.views || 0),
                likes: Math.max(0, item.likes || 0),
                comments: Math.max(0, item.comments || 0),
                shares: Math.max(0, item.shares || 0),
                posts: Math.max(0, item.post_count || 0),
                color: platformColor(item.platform, index),
                engagement:
                    (item.views || 0) > 0
                        ? Math.round(
                            ((item.likes || 0) + (item.comments || 0)) / item.views * 10000
                        ) / 100
                        : 0,
            })),
        [analytics]
    );

    const totalStats = useMemo(() => ({
        views: chartData.reduce((s, d) => s + d.views, 0),
        likes: chartData.reduce((s, d) => s + d.likes, 0),
        comments: chartData.reduce((s, d) => s + d.comments, 0),
        shares: chartData.reduce((s, d) => s + d.shares, 0),
        posts: chartData.reduce((s, d) => s + d.posts, 0),
        platforms: chartData.length,
    }), [chartData]);

    const avgEngagement = useMemo(() => {
        if (!chartData.length) return 0;
        const sum = chartData.reduce((s, d) => s + d.engagement, 0);
        return Math.round(sum / chartData.length * 100) / 100;
    }, [chartData]);

    const pieData = useMemo(() =>
        chartData
            .filter(d => d.views > 0)
            .map(d => ({ name: d.platform, value: d.views, color: d.color })),
        [chartData]
    );

    // ── Guard states ────────────────────────────────────────────────────────
    if (loading) return <Box><PageAlert error={error} /><DashboardLoading /></Box>;
    if (isNoAccountsError) return (
        <Box sx={{ py: 6 }}>
            <ConnectPlatformsCTA
                title="Dashboard Locked"
                description="Connect your social media accounts to see live analytics, engagement trends, and growth metrics."
            />
        </Box>
    );
    if (!analytics || analytics.length === 0) return <Box><PageAlert error={error} /><DashboardEmpty onRefresh={onRefresh} /></Box>;

    if (!ViewDistributionPieChart) throw new Error('ViewDistributionPieChart is undefined');
    if (!ViewsBarChart) throw new Error('ViewsBarChart is undefined');
    if (!LikesCommentsChart) throw new Error('LikesCommentsChart is undefined');
    if (!EngagementRateChart) throw new Error('EngagementRateChart is undefined');

    // ── Full dashboard ──────────────────────────────────────────────────────
    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
            <PageAlert error={error} />
            {/* Page header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: { xs: 4, md: 6 },
                    flexWrap: 'wrap',
                    gap: 3,
                }}
            >
                <Box>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 900,
                            letterSpacing: '-0.03em',
                            mb: 1,
                            fontSize: { xs: '1.75rem', md: '2.5rem' },
                            background: 'linear-gradient(135deg, #1e293b, #3b82f6)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Analytics Dashboard
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Performance overview across <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>{totalStats.platforms}</Box> active channels
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    size="large"
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                    sx={{
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        boxShadow: '0 8px 20px -4px rgba(99, 102, 241, 0.4)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                            boxShadow: '0 12px 28px -4px rgba(99, 102, 241, 0.5)',
                            transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    Update Analytics
                </Button>
            </Box>

            {/* Sections */}
            {/* Sections */}
            <Grid container spacing={3}>
                {/* Row 1: KPI Grid (12 cols, 4 per row on Desktop) */}
                <Grid xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Total Views"
                        value={totalStats.views}
                        icon={<VisibilityIcon sx={{ fontSize: 26 }} />}
                        color="#6366f1"
                        subtitle="Impressions across all platforms"
                    />
                </Grid>
                <Grid xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Total Likes"
                        value={totalStats.likes}
                        icon={<ThumbUpIcon sx={{ fontSize: 26 }} />}
                        color="#0284c7"
                        subtitle="Engagement received"
                    />
                </Grid>
                <Grid xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Total Comments"
                        value={totalStats.comments}
                        icon={<CommentIcon sx={{ fontSize: 26 }} />}
                        color="#0d9488"
                        subtitle="Community interaction"
                    />
                </Grid>
                <Grid xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Avg Engagement"
                        value={`${avgEngagement}%`}
                        icon={<TrendingUpIcon sx={{ fontSize: 26 }} />}
                        color="#059669"
                        subtitle="(Likes + Comments) / Views"
                    />
                </Grid>
                <Grid xs={12}>
                    <ViewDistributionPieChart pieData={pieData} height={100} />
                </Grid>
            </Grid>
            {/* Row 2: Secondary Analysis */}
            <Grid container sx={{ mt: 6 }}>
                <Grid size={{ xs: 12, lg: 12 }}>
                    <ViewsBarChart chartData={chartData} height={250} />
                </Grid>
            </Grid>
            {/* Row 3 – Full-width platform table */}
            <Grid container sx={{ mt: 6 }}>
                <Grid size={{ xs: 12, lg: 12 }}>
                    <LikesCommentsChart chartData={chartData} height={250} />
                </Grid>
            </Grid>
            {/* Row 4 – Full-width platform table */}
            <Grid container sx={{ mt: 6 }}>
                <Grid size={{ xs: 12, lg: 12 }}>
                    <PlatformTable chartData={chartData} />
                </Grid>
            </Grid>
        </Container>
    );
};

export default Dashboard;
